/**
 * FS Subscription Module
 *
 * Handles order-level subscription functionality:
 * - Cart order toggle (One-time vs Subscribe & Save)
 * - Frequency selection for all subscription items
 * - Batch update all items when toggling subscription
 * - Cart section refresh without triggering validation errors
 */

(function() {
  'use strict';

  const FS_SUBSCRIPTION_PREF_KEY = 'fs_subscription_preference';
  const FS_FREQUENCY_PREF_KEY = 'fs_subscription_frequency';

  /**
   * Get stored subscription preference
   * @returns {boolean} - Whether subscription was previously selected
   */
  function getStoredPreference() {
    try {
      return localStorage.getItem(FS_SUBSCRIPTION_PREF_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Store subscription preference
   * @param {boolean} isSubscription - Whether subscription is selected
   */
  function setStoredPreference(isSubscription) {
    try {
      localStorage.setItem(FS_SUBSCRIPTION_PREF_KEY, isSubscription ? 'true' : 'false');
    } catch (e) {
      // localStorage not available
    }
  }

  /**
   * Get stored frequency preference from localStorage
   * @returns {string|null} The stored selling plan ID or null
   */
  function getStoredFrequency() {
    try {
      return localStorage.getItem(FS_FREQUENCY_PREF_KEY);
    } catch (e) {
      return null;
    }
  }

  /**
   * Store frequency preference in localStorage
   * @param {string} sellingPlanId - The selling plan ID to store
   */
  function setStoredFrequency(sellingPlanId) {
    try {
      localStorage.setItem(FS_FREQUENCY_PREF_KEY, sellingPlanId);
    } catch (e) {
      // localStorage not available
    }
  }

  /**
   * Fetch fresh cart data from Shopify
   * @returns {Promise<Object>} - The cart data
   */
  async function fetchCartData() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return response.json();
  }

  /**
   * Update line item selling plan via Cart API
   * @param {string} lineKey - The cart line item key
   * @param {string|null} sellingPlanId - The selling plan ID or empty string for one-time
   * @returns {Promise<Object>} - The updated cart data
   */
  async function updateLineItemSellingPlan(lineKey, sellingPlanId) {
    const body = JSON.stringify({
      id: lineKey,
      selling_plan: sellingPlanId || ''
    });

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });

    if (!response.ok) {
      throw new Error('Failed to update line item');
    }

    const cart = await response.json();

    // Check for errors in the response
    if (cart.errors) {
      console.error('Cart API error:', cart.errors);
      throw new Error(cart.errors);
    }

    return cart;
  }

  /**
   * Refresh the cart drawer/page sections after subscription changes
   * Directly fetches and updates sections to avoid validation errors
   */
  async function refreshCartSections() {
    try {
      // Check if we're in the cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerItems = document.querySelector('cart-drawer-items');

      if (cartDrawerItems) {
        // Refresh cart drawer
        const response = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update cart drawer content
        const newDrawerInner = doc.querySelector('.drawer__inner');
        const currentDrawerInner = document.querySelector('cart-drawer .drawer__inner');
        if (newDrawerInner && currentDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }

        // Re-initialize the subscription toggle after refresh
        requestAnimationFrame(() => {
          const newSubscriptionToggle = document.querySelector('[data-cart-order-subscription]');
          if (newSubscriptionToggle) {
            newSubscriptionToggle.dataset.initialized = '';
            initOrderSubscriptionToggle();
          }
        });
      }

      // Check if we're on the /cart page
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        const response = await fetch(`${window.location.pathname}?section_id=main-cart-items`);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newContent = doc.querySelector('.js-contents');
        const currentContent = mainCartItems.querySelector('.js-contents');
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }

        // Also update the subscription toggle if present
        const newSubscriptionSection = doc.querySelector('[data-cart-order-subscription]');
        const currentSubscriptionSection = document.querySelector('#main-cart-items [data-cart-order-subscription], .cart__contents [data-cart-order-subscription]');
        if (newSubscriptionSection && currentSubscriptionSection) {
          currentSubscriptionSection.outerHTML = newSubscriptionSection.outerHTML;
        }

        // Re-initialize
        requestAnimationFrame(() => {
          const subscriptionToggle = document.querySelector('[data-cart-order-subscription]');
          if (subscriptionToggle) {
            subscriptionToggle.dataset.initialized = '';
            initOrderSubscriptionToggle();
          }
        });
      }

      // Update cart count in header
      const cartCountBubble = document.querySelector('.cart-count-bubble');
      if (cartCountBubble) {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        const countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
        if (countSpan) {
          countSpan.textContent = cartData.item_count;
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart sections:', error);
    }
  }

  /**
   * Initialize a single order subscription toggle container
   * @param {HTMLElement} container - The container element
   */
  function initSingleOrderSubscriptionToggle(container) {
    if (!container) return;
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    const radios = container.querySelectorAll('[data-subscription-radio]');
    const frequencySection = container.querySelector('[data-frequency-section]');
    const frequencySelect = container.querySelector('[data-frequency-select]');
    const totalsSection = container.querySelector('[data-totals-section]');

    // Find the items data within or near this container
    let itemsDataEl = container.querySelector('[data-cart-items-subscription-data]');
    if (!itemsDataEl) {
      // Try to find it as a sibling
      itemsDataEl = container.parentElement?.querySelector('[data-cart-items-subscription-data]');
    }
    if (!itemsDataEl) {
      // Fallback to global search
      itemsDataEl = document.querySelector('[data-cart-items-subscription-data]');
    }

    if (!itemsDataEl) return;

    let cartItems = [];
    try {
      cartItems = JSON.parse(itemsDataEl.textContent);
      // Filter out any empty or invalid entries
      cartItems = cartItems.filter(item => item && item.key);
    } catch (e) {
      console.error('Failed to parse cart items data:', e);
      return;
    }

    if (cartItems.length === 0) return;

    // Sync from localStorage on load
    const storedFrequency = getStoredFrequency();
    if (storedFrequency && frequencySelect) {
      const storedOption = frequencySelect.querySelector(`option[value="${storedFrequency}"]`);
      if (storedOption) {
        frequencySelect.value = storedFrequency;
      }
    }

    // Handle toggle change (one-time vs subscription)
    radios.forEach(radio => {
      radio.addEventListener('change', async () => {
        const isSubscription = radio.value === 'subscription';

        // Update active state on labels within this container
        container.querySelectorAll('.fs-cart-order-subscription__option').forEach(opt => {
          opt.classList.remove('fs-cart-order-subscription__option--active');
        });
        radio.closest('.fs-cart-order-subscription__option').classList.add('fs-cart-order-subscription__option--active');

        // Show/hide frequency section and totals section
        if (frequencySection) {
          frequencySection.style.display = isSubscription ? 'flex' : 'none';
        }
        if (totalsSection) {
          totalsSection.style.display = isSubscription ? 'flex' : 'none';
        }

        // Toggle is-subscription class on all containers to hide default totals
        document.querySelectorAll('[data-cart-order-subscription]').forEach(c => {
          c.classList.toggle('is-subscription', isSubscription);
        });

        // Store preference
        setStoredPreference(isSubscription);

        // Get selected selling plan ID (from dropdown or from stored preference or from first item)
        let sellingPlanId = null;
        if (isSubscription) {
          if (frequencySelect && frequencySelect.value) {
            sellingPlanId = frequencySelect.value;
          } else if (storedFrequency) {
            sellingPlanId = storedFrequency;
          } else {
            // Use the first eligible item's default selling plan
            const firstEligible = cartItems.find(item => item.hasSellingPlan);
            if (firstEligible) {
              sellingPlanId = firstEligible.sellingPlanId;
            }
          }
        }

        // Add loading state to ALL containers
        document.querySelectorAll('[data-cart-order-subscription]').forEach(c => {
          c.classList.add('is-loading');
        });

        try {
          // Fetch fresh cart data to get current line keys
          const freshCart = await fetchCartData();

          // Update ALL eligible items using fresh cart data
          for (const item of freshCart.items) {
            // Check if item has selling plans by looking at product properties
            // We need to check if the item can have a selling plan
            const cachedItem = cartItems.find(ci => ci.variantId === item.variant_id);
            if (cachedItem && cachedItem.hasSellingPlan) {
              const planId = isSubscription ? (sellingPlanId || cachedItem.sellingPlanId) : '';
              await updateLineItemSellingPlan(item.key, planId);
            }
          }

          // Refresh cart sections after all updates complete
          await refreshCartSections();
        } catch (error) {
          console.error('Failed to update subscription status:', error);
          // Refresh anyway to show current state
          await refreshCartSections();
        } finally {
          document.querySelectorAll('[data-cart-order-subscription]').forEach(c => {
            c.classList.remove('is-loading');
          });
        }
      });
    });

    // Handle frequency dropdown change
    if (frequencySelect) {
      frequencySelect.addEventListener('change', async () => {
        const newSellingPlanId = frequencySelect.value;

        // Store preference in localStorage for syncing
        setStoredFrequency(newSellingPlanId);

        // Add loading state to ALL containers
        document.querySelectorAll('[data-cart-order-subscription]').forEach(c => {
          c.classList.add('is-loading');
        });

        try {
          // Fetch fresh cart data to get current line keys
          const freshCart = await fetchCartData();

          // Update all subscription items to the new frequency
          for (const item of freshCart.items) {
            // Only update items that are currently subscriptions
            if (item.selling_plan_allocation) {
              await updateLineItemSellingPlan(item.key, newSellingPlanId);
            }
          }

          // Refresh cart sections after all updates complete
          await refreshCartSections();
        } catch (error) {
          console.error('Failed to update subscription frequency:', error);
          // Refresh anyway to show current state
          await refreshCartSections();
        } finally {
          document.querySelectorAll('[data-cart-order-subscription]').forEach(c => {
            c.classList.remove('is-loading');
          });
        }
      });
    }
  }

  /**
   * Initialize order-level subscription toggle
   * Handles toggling entire order between one-time and subscription
   * Supports multiple instances (cart drawer + cart page)
   */
  function initOrderSubscriptionToggle() {
    const containers = document.querySelectorAll('[data-cart-order-subscription]');
    containers.forEach(container => {
      initSingleOrderSubscriptionToggle(container);
    });
  }

  /**
   * Initialize all subscription functionality
   */
  function init() {
    initOrderSubscriptionToggle();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after cart updates from OTHER sources (not our own)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Skip if this is our own update
      if (event?.source === 'fs-subscription') return;

      // Wait for DOM update, then refresh sections to get updated savings
      requestAnimationFrame(async () => {
        // Refresh cart sections to get updated savings calculations from Liquid
        await refreshCartSections();
      });
    });
  }

  // Export for external use
  window.FSSubscription = {
    init,
    initOrderSubscriptionToggle,
    updateLineItemSellingPlan,
    refreshCartSections,
    getStoredPreference,
    setStoredPreference,
    getStoredFrequency,
    setStoredFrequency
  };
})();
